import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Pulsante "Salva ricerca" — apre modal con nome + salva i filtri correnti.
 * Riceve `filters` (object) coi filtri della pagina Explore.
 */
export default function SaveSearchButton({ filters, size = 'default' }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const suggest = () => {
    const parts = [];
    if (filters?.region) parts.push(filters.region);
    if (filters?.listing_type === 'rent') parts.push('affitto');
    if (filters?.listing_type === 'sale') parts.push('vendita');
    if (filters?.crop) parts.push(filters.crop);
    if (filters?.price_max) parts.push(`<${Math.round(filters.price_max/1000)}k€`);
    return parts.length ? parts.join(' · ') : 'La mia ricerca';
  };

  const openModal = () => {
    if (!user) { nav('/login'); return; }
    if (!filters || Object.values(filters).every(v => !v || (Array.isArray(v) && !v.length))) {
      toast.error('Imposta almeno un filtro prima di salvare');
      return;
    }
    setName(suggest());
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/saved-searches', { name: name.trim() || suggest(), filters });
      toast.success('Ricerca salvata — riceverai email quando compare un nuovo terreno che matcha');
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size={size} onClick={openModal} className="rounded-full" data-testid="save-search-btn">
        <Bell size={13} className="mr-2"/> Salva ricerca
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-obsidian/70 backdrop-blur-sm grid place-items-center px-4" onClick={()=>setOpen(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8 max-w-md w-full" onClick={e=>e.stopPropagation()} data-testid="save-search-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-terracotta">Alert Terreno Salvato</div>
                <h3 className="font-display text-xl mt-1">Salva questa ricerca</h3>
              </div>
              <button onClick={()=>setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={18}/></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Ti mandiamo un'email a <b>{user?.email}</b> ogni volta che compare un nuovo terreno che matcha questi filtri.</p>
            <div className="space-y-2 mb-5">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Nome ricerca</label>
              <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Es. Uliveti Toscana sotto 100k" maxLength={100} data-testid="save-search-name"/>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={()=>setOpen(false)} className="rounded-full">Annulla</Button>
              <Button size="sm" onClick={submit} disabled={busy || !name.trim()} className="rounded-full" data-testid="save-search-confirm">
                {busy ? 'Salvo…' : 'Salva e attiva alert'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
